import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchMe, loginWithGoogleCredential } from '../api';
import { updateSocketAuth } from '../socket';

const AUTH_TOKEN_KEY = 'pp_auth_token';
const AUTH_USER_KEY = 'pp_auth_user';

const AuthContext = createContext(null);

function readStoredAuth() {
    try {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        const userRaw = localStorage.getItem(AUTH_USER_KEY);
        if (!token || !userRaw) return { token: null, user: null };
        return { token, user: JSON.parse(userRaw) };
    } catch {
        return { token: null, user: null };
    }
}

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => readStoredAuth().token);
    const [user, setUser] = useState(() => readStoredAuth().user);
    const [loading, setLoading] = useState(false);

    const persistAuth = useCallback((nextToken, nextUser) => {
        if (nextToken && nextUser) {
            localStorage.setItem(AUTH_TOKEN_KEY, nextToken);
            localStorage.setItem(AUTH_USER_KEY, JSON.stringify(nextUser));
        } else {
            localStorage.removeItem(AUTH_TOKEN_KEY);
            localStorage.removeItem(AUTH_USER_KEY);
        }
        setToken(nextToken);
        setUser(nextUser);
        updateSocketAuth(nextToken);
    }, []);

    const loginWithGoogle = useCallback(async (credential) => {
        setLoading(true);
        try {
            const { token: appToken, user: appUser } = await loginWithGoogleCredential(credential);
            persistAuth(appToken, appUser);
            return appUser;
        } finally {
            setLoading(false);
        }
    }, [persistAuth]);

    const logout = useCallback(() => {
        persistAuth(null, null);
    }, [persistAuth]);

    useEffect(() => {
        updateSocketAuth(token);
    }, [token]);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;

        (async () => {
            try {
                const { user: freshUser } = await fetchMe(token);
                if (!cancelled && freshUser) {
                    setUser(freshUser);
                    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(freshUser));
                }
            } catch {
                if (!cancelled) {
                    persistAuth(null, null);
                }
            }
        })();

        return () => { cancelled = true; };
    }, [token, persistAuth]);

    const value = useMemo(() => ({
        user,
        token,
        isAuthenticated: Boolean(token && user),
        isAdmin: Boolean(user?.isAdmin),
        loading,
        loginWithGoogle,
        logout,
    }), [user, token, loading, loginWithGoogle, logout]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth deve ser usado dentro de AuthProvider');
    }
    return ctx;
}
